import React, { Component } from 'react';
import { Link, NavLink, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import classlist from 'utils/classlist';
import { Collapse } from 'components/Transition';
import security from 'stores/security';
import LangSwitch from 'components/LangSwitch';
import ui from 'stores/ui';
import API from 'utils/API';
import withI18n, { I18nProps } from 'utils/i18n/withI18n';
import MenuSwitch from './MenuSwitch';
import pc from './images/pc.png';
import './style.scss';

interface HeaderState {
    showNavMenu: boolean;
    showLanguageMenu: boolean;
    isFloat: boolean;
    duration: number; // 动画持续时间
}

// 匹配的是移动端尺寸,包含Ipad
const mediaQueryOfScreen = typeof window === 'undefined' || window.matchMedia('(max-width: 768px)');

@observer
class Header extends Component<RouteComponentProps & I18nProps, HeaderState> {
    readonly state = {
        showNavMenu: !(mediaQueryOfScreen as MediaQueryList).matches,
        showLanguageMenu: false,
        isFloat: false,
        duration: 0
    };

    // header的DOM
    headerNode: HTMLElement;

    componentDidMount() {
        // 移动端点击空白关闭导航栏
        document.addEventListener('click', this.hideNavMenu, false);

        // 监听屏幕切换事件
        (mediaQueryOfScreen as MediaQueryList).addListener(this.switchScreen);

        window.addEventListener('scroll', this.detectFloat, false);
        window.addEventListener('resize', this.detectFloat, false);

        this.detectFloat();
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.hideNavMenu, false);
        (mediaQueryOfScreen as MediaQueryList).removeListener(this.switchScreen);
    }

    // 判断是否向下滚动
    detectFloat = _.throttle((ev?: Event) => {
        this.setState({
            isFloat: window.scrollY >= 10
        });
    });

    hideNavMenu = (event?: MouseEvent) => {
        if (
            (mediaQueryOfScreen as MediaQueryList).matches &&
            !!this.headerNode &&
            !this.headerNode.contains(event?.target as Node) &&
            this.state.showNavMenu
        ) {
            this.toggleShowNavMenu(false);
        }
    };

    toggleShowNavMenu = (status?: boolean) => {
        const showNavMenu = typeof status === 'boolean' ? status : !this.state.showNavMenu;

        this.setState({ showNavMenu });
    };

    toggleShowLanguageMenu = (status?: boolean) => {
        const showLanguageMenu = typeof status === 'boolean' ? status : !this.state.showLanguageMenu;

        this.setState({
            showLanguageMenu
        });
    };

    hideNavAndLanguageMenu = (event?: MouseEvent) => {
        if (
            (mediaQueryOfScreen as MediaQueryList)?.matches &&
            !!this.headerNode &&
            !this.headerNode.contains(event?.target as Node) &&
            this.state.showNavMenu
        ) {
            this.toggleShowNavMenu(false);
        }

        if (!!this.headerNode && !this.headerNode.contains(event?.target as Node)) {
            this.toggleShowLanguageMenu(false);
        }
    };

    // 根据屏幕的切换控制菜单栏的显隐
    switchScreen = mediaQuery => {
        const { duration } = this.state;
        // 关闭切换屏幕下拉菜单动画

        if (duration !== 0) {
            this.setState({
                duration: 0
            });
        }

        this.toggleShowNavMenu(!mediaQuery.matches);
    };

    getHeaderRef = node => {
        this.headerNode = node;
    };

    handleMenu = () => {
        const { duration } = this.state;
        // 用户点击按钮时开启下拉菜单动画

        if (duration !== 600) {
            this.setState({
                duration: 600
            });
        }
    };

    render() {
        const { showNavMenu, duration, showLanguageMenu } = this.state;
        const {
            i18n: { language },
            location: { pathname }
        } = this.props;
        const allowLang = pathname.startsWith('/help') || pathname.startsWith('/pricing');

        return (
            <header
                className={classlist('site-header', `site-header-${ui.navTheme}`, {
                    'site-header-float': this.state.isFloat
                })}
                ref={this.getHeaderRef}>
                <div className="site-main">
                    <nav className="site-nav">
                        <h1 className="site-primary-brand">
                            <Link to="/" id="logo">
                                TradeUP
                            </Link>
                        </h1>
                        <div className="header-nav-box">
                            <Collapse in={showNavMenu} timeout={duration}>
                                <nav
                                    className={classlist('header-nav-container', {
                                        'nav-hidden': !(mediaQueryOfScreen as MediaQueryList).matches
                                    })}
                                    onClick={() => {
                                        this.hideNavAndLanguageMenu();
                                    }}>
                                    <NavLink className="nav-link" exact to="/">
                                        Investing
                                    </NavLink>
                                    <NavLink exact to="/platform" className="nav-link">
                                        Platform
                                    </NavLink>
                                    <NavLink exact to="/about" className="nav-link">
                                        About Us
                                    </NavLink>
                                    <NavLink to="/pricing/commissions-us" className="nav-link">
                                        Pricing
                                    </NavLink>
                                    <NavLink to="/help" className="nav-link">
                                        Help
                                    </NavLink>
                                    {/* isLogin代表当前的登录状态 */}
                                    {security.isLogin ? (
                                        <>
                                            <a href="/personal" className="btn btn-account" key="account">
                                                My Account
                                            </a>
                                        </>
                                    ) : (
                                        <>
                                            <a href="/login" className="nav-link">
                                                Log In
                                            </a>
                                            <a
                                                href="/accounts"
                                                className="btn btn-account"
                                                key="account"
                                                target="_blank">
                                                Open Account
                                            </a>
                                        </>
                                    )}
                                    <a className="u-web-app btn" href={API.PC.host()} target="view_window">
                                        <img src={pc} alt="pc" title="pc" />
                                        <span>Web Trading</span>
                                    </a>
                                    {allowLang && (
                                        <LangSwitch
                                            language={language}
                                            onClick={ev => {
                                                ev.preventDefault();
                                                ev.stopPropagation();

                                                this.toggleShowLanguageMenu();
                                            }}
                                            showLanguageMenu={showLanguageMenu}
                                        />
                                    )}
                                </nav>
                            </Collapse>
                            <MenuSwitch
                                opened={
                                    typeof window === 'undefined' || !(mediaQueryOfScreen as MediaQueryList).matches
                                        ? false
                                        : showNavMenu
                                }
                                onClick={() => {
                                    this.handleMenu();
                                    this.toggleShowNavMenu();
                                }}
                            />
                        </div>
                    </nav>
                </div>
            </header>
        );
    }
}

export default withI18n(Header);
